import moment from 'moment';
import React from 'react';
import { generatePath } from 'react-router';
import {
  PatientConditionsCard,
  PatientConditionsCardItem,
} from '../../../../components/Cards';
import { useCarePlan } from '../../../../contexts/carePlan';
import { usePatient } from '../../../../contexts/patient';
import { QuestionnaireContext } from '../../../../contexts/questionnaire';
import { QuestionnaireResponseContext } from '../../../../contexts/questionnaireResponse';
import { getReferenceId } from '../../../../modules/unsw-fhir/Reference';
import QuestionnaireProvider from '../../../Providers/Questionnaire';
import QuestionnaireResponseProvider from '../../../Providers/QuestionnnaireResponse';

const ConditionResponseItem = ({
  questionnaireResponseId,
  patientId,
  organizationId,
}: {
  questionnaireResponseId: string;
  patientId: string;
  organizationId: string;
}) => (
  <QuestionnaireResponseProvider
    questionnaireResponseId={questionnaireResponseId}>
    <QuestionnaireResponseContext.Consumer>
      {({
        questionnaireLoading,
        questionnaireResponseLoading,
        questionnaireResponse,
        questionnaire,
        error,
      }) => {
        const title = questionnaire && questionnaire.title;
        const created =
          questionnaireResponse &&
          moment(questionnaireResponse.authored!).format('DD-MM-YYYY');

        const params = {
          patientId,
          organizationId,
          questionnaireResponseId: questionnaireResponse?.id!,
        };

        const viewPath = questionnaireResponse
          ? generatePath(
              '/organization/:organizationId/patient/:patientId/questionnaireResponse/:questionnaireResponseId',
              params,
            )
          : undefined;

        const editPath = questionnaireResponse
          ? generatePath(
              '/organization/:organizationId/patient/:patientId/questionnaireResponse/:questionnaireResponseId/edit',
              params,
            )
          : undefined;

        return (
          <PatientConditionsCardItem
            title={title || '-'}
            created={created || '-'}
            editPath={editPath}
            viewPath={viewPath}
            loading={questionnaireLoading || questionnaireResponseLoading}
            disabled={!!error}
          />
        );
      }}
    </QuestionnaireResponseContext.Consumer>
  </QuestionnaireResponseProvider>
);

const ConditionQuestionnaireItem = ({
  questionnaireId,
  organizationId,
  patientId,
}: {
  questionnaireId: string;
  patientId: string;
  organizationId: string;
}) => {
  return (
    <QuestionnaireProvider questionnaireId={questionnaireId}>
      <QuestionnaireContext.Consumer>
        {({ loading, getQuestionnaireTitle, getQuestionnaireName, error }) => {
          const params = {
            patientId,
            organizationId,
            questionnaireName: getQuestionnaireName(),
          };
          const newPath = patientId
            ? generatePath(
                '/organization/:organizationId/patient/:patientId/newQuestionnaireResponse/:questionnaireName',
                params,
              )
            : undefined;
          return (
            <PatientConditionsCardItem
              title={getQuestionnaireTitle()}
              newPath={newPath}
              disabled={!!error}
              loading={loading}
            />
          );
        }}
      </QuestionnaireContext.Consumer>
    </QuestionnaireProvider>
  );
};

const ConditionItem = ({
  questionnaireId,
  questionnaireResponseId,
  patientId,
  organizationId,
}: {
  questionnaireId: string;
  questionnaireResponseId?: string;
  patientId: string;
  organizationId: string;
}) => {
  return questionnaireResponseId ? (
    <ConditionResponseItem
      questionnaireResponseId={questionnaireResponseId}
      patientId={patientId}
      organizationId={organizationId}
    />
  ) : (
    <ConditionQuestionnaireItem
      questionnaireId={questionnaireId}
      patientId={patientId}
      organizationId={organizationId}
    />
  );
};

const PatientConditions = () => {
  const { carePlan, loading: carePlanLoading } = useCarePlan();
  const { patient, loading: patientLoading } = usePatient();
  const loading = carePlanLoading || patientLoading;
  const conditions =
    carePlan && carePlan.activity
      ? carePlan.activity
          .filter(
            activity => activity.detail && activity.detail.reasonReference,
          )
          .map(activity => ({
            questionnaireId: getReferenceId(activity.detail!.definition)!,
            responseId: getReferenceId(
              activity.outcomeReference && activity.outcomeReference[0],
            ),
          }))
      : [];

  return (
    <PatientConditionsCard
      loading={loading}
      notApplicable={!loading && conditions.length === 0}>
      {patient
        ? conditions.map(condition => (
            <ConditionItem
              key={condition.questionnaireId}
              questionnaireId={condition.questionnaireId}
              questionnaireResponseId={condition.responseId}
              patientId={patient.id!}
              organizationId={getReferenceId(patient.managingOrganization)!}
            />
          ))
        : undefined}
    </PatientConditionsCard>
  );
};

export default PatientConditions;
